import { useMemo, useState, useEffect, useCallback } from "react"
import { Search, Download, Trash2, Send, Copy, ChevronLeft, ChevronRight } from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "../components/ui/dialog"
import { Badge } from "../components/ui/badge"
import { useLogs } from "../components/LogProvider"
import { useCounts } from "../components/CountProvider"
import { useLocale } from "../components/LocaleProvider"
import { api } from "../lib/api"
import type { LogDuplicatesResponse } from "../lib/api"
import { toast } from "../components/ui/toast"
import type { LogAction, LogEntry } from "../types"
import type { TranslationKey } from "../i18n/translations"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import { loadPdfFont } from "../lib/pdf"
import { Switch } from "../components/ui/switch"

const ACTION_VARIANT: Record<LogAction, "success" | "info" | "warning"> = {
  add: "success",
  edit: "info",
  queued: "warning",
}

const ACTION_T_KEY: Record<LogAction, TranslationKey> = {
  add: "log_action_add",
  edit: "log_action_edit",
  queued: "log_action_queued",
}

const PAGE_SIZE = 50

function parseDetails(details: string) {
  const parts = details.split(" | ")
  if (parts.length === 5) {
    return {
      name: parts[0].trim(),
      size: parts[1].trim(),
      rooms: parts[2].trim(),
      price: parts[3].trim(),
      location: parts[4].trim(),
    }
  }
  return { name: details, size: "", rooms: "", price: "", location: "" }
}

const PDF_FIELDS = [
  { key: "timestamp", labelKey: "th_time" as TranslationKey },
  { key: "user", labelKey: "th_user" as TranslationKey },
  { key: "page", labelKey: "th_page" as TranslationKey },
  { key: "action", labelKey: "th_action" as TranslationKey },
  { key: "client", labelKey: "th_client" as TranslationKey },
  { key: "accountEmail", labelKey: "th_account_email" as TranslationKey },
  { key: "size", labelKey: "th_sqft" as TranslationKey },
  { key: "price", labelKey: "th_price" as TranslationKey },
  { key: "rooms", labelKey: "th_rooms" as TranslationKey },
  { key: "name", labelKey: "th_name" as TranslationKey },
  { key: "location", labelKey: "th_location" as TranslationKey },
] as const

type PdfFieldKey = (typeof PDF_FIELDS)[number]["key"]

function Logs() {
  const { logs, refetch } = useLogs()
  const { setCount } = useCounts()
  const { t } = useLocale()
  const [search, setSearch] = useState("")
  const [clientFilter, setClientFilter] = useState("")
  const [page, setPage] = useState(1)
  const [pdfModalOpen, setPdfModalOpen] = useState(false)
  const [clearModalOpen, setClearModalOpen] = useState(false)
  const [clearClient, setClearClient] = useState("")
  const [clearConfirmText, setClearConfirmText] = useState("")
  const [sendModalOpen, setSendModalOpen] = useState(false)
  const [sendConfirmText, setSendConfirmText] = useState("")
  const [sendingReport, setSendingReport] = useState(false)
  const [dupModalOpen, setDupModalOpen] = useState(false)
  const [dupData, setDupData] = useState<LogDuplicatesResponse | null>(null)
  const [dupLoading, setDupLoading] = useState(false)
  const [dupConfirmText, setDupConfirmText] = useState("")
  const [removingDup, setRemovingDup] = useState(false)
  const [selectedFields, setSelectedFields] = useState<PdfFieldKey[]>([
    "timestamp",
    "client",
    "size",
    "price",
    "name",
  ])

  const uniqueClients = useMemo(
    () => Array.from(new Set(logs.map((r) => r.client).filter(Boolean))).sort(),
    [logs],
  )

  const clientFiltered = useMemo(
    () => (clientFilter ? logs.filter((r) => r.client === clientFilter) : logs),
    [logs, clientFilter],
  )

  useEffect(() => {
    setCount("logs", logs.length)
  }, [logs.length, setCount])

  const filtered = useMemo(() => {
    return clientFiltered.filter(
      (row) =>
        row.action === "add" &&
        Object.values(row).some((v) => String(v).toLowerCase().includes(search.toLowerCase())),
    )
  }, [clientFiltered, search])

  useEffect(() => {
    setPage(1)
  }, [search, clientFilter])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const paged = useMemo(
    () => filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filtered, currentPage],
  )

  const getFieldValue = (row: LogEntry, key: PdfFieldKey): string => {
    if (key === "action") return t(ACTION_T_KEY[row.action])
    if (key === "accountEmail") return row.accountEmail || "—"
    if (key === "timestamp") {
      const d = new Date(row.timestamp)
      if (isNaN(d.getTime())) return String(row.timestamp ?? "—")
      return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
    }
    if (
      key === "size" ||
      key === "price" ||
      key === "rooms" ||
      key === "name" ||
      key === "location"
    ) {
      const parsed = parseDetails(row.details)
      return parsed ? parsed[key] : "—"
    }
    return String(row[key as keyof LogEntry] ?? "—")
  }

  const sortedFields = useMemo(
    () =>
      [...selectedFields].sort(
        (a, b) =>
          PDF_FIELDS.findIndex((f) => f.key === a) - PDF_FIELDS.findIndex((f) => f.key === b),
      ),
    [selectedFields],
  )

  const pdfFiltered = useMemo(() => filtered, [filtered])

  const clearLogsCount = useMemo(
    () => (clearClient ? logs.filter((r) => r.client === clearClient).length : logs.length),
    [logs, clearClient],
  )

  const refreshDuplicates = useCallback(async () => {
    try {
      const data = await api.getLogDuplicates(clientFilter || undefined)
      setDupData(data)
    } catch {
      // badge simply won't show; duplicates still checked on modal open
    }
  }, [clientFilter])

  useEffect(() => {
    refreshDuplicates()
  }, [refreshDuplicates])

  const handleClearLogs = async () => {
    try {
      const { deleted } = await api.clearLogs(clearClient || undefined)
      toast(t("toast_logs_cleared").replace("{n}", String(deleted)))
      await refetch()
      await refreshDuplicates()
      setClearModalOpen(false)
      setClearClient("")
      setClearConfirmText("")
    } catch (err: any) {
      toast(err.message, "error")
    }
  }

  const handleSendReportToTelegram = async () => {
    if (!clientFilter) return
    setSendingReport(true)
    try {
      const res = await api.sendClientReport(clientFilter)
      toast(
        t("report_sent")
          .replace("{records}", String(res.records))
          .replace("{chat}", String(res.chatId)),
      )
      setSendModalOpen(false)
      setSendConfirmText("")
    } catch (err: any) {
      toast(err.message, "error")
    } finally {
      setSendingReport(false)
    }
  }

  const openSendReportModal = () => {
    if (!clientFilter) {
      toast(t("report_select_client"), "error")
      return
    }
    setSendConfirmText("")
    setSendModalOpen(true)
  }

  const openDuplicatesModal = async () => {
    setDupConfirmText("")
    setDupModalOpen(true)
    setDupLoading(true)
    setDupData(null)
    try {
      const data = await api.getLogDuplicates(clientFilter || undefined)
      setDupData(data)
    } catch (err: any) {
      toast(err.message, "error")
      setDupModalOpen(false)
    } finally {
      setDupLoading(false)
    }
  }

  const handleRemoveDuplicates = async () => {
    setRemovingDup(true)
    try {
      const { deleted } = await api.removeLogDuplicates(clientFilter || undefined)
      toast(t("toast_duplicates_removed").replace("{n}", String(deleted)))
      setDupModalOpen(false)
      setDupConfirmText("")
      await refetch()
      await refreshDuplicates()
    } catch (err: any) {
      toast(err.message, "error")
    } finally {
      setRemovingDup(false)
    }
  }

  const handleDownloadPdf = async () => {
    if (selectedFields.length === 0) return
    const doc = new jsPDF("l", "mm", "a4")
    await loadPdfFont(doc)
    doc.setFont("DejaVuSans")

    const label = clientFilter || t("filter_all_clients")
    const suffix = "_add"
    const title = `${t("log_pdf_title")} — ${label} (${t("log_action_add")})`
    const dateStr = new Date().toLocaleDateString("de-DE")

    doc.setFontSize(16)
    doc.text(title, 14, 15)
    doc.setFontSize(10)
    doc.setTextColor(120)
    doc.text(dateStr, 14, 22)

    const headers = sortedFields.map((k) => {
      const field = PDF_FIELDS.find((f) => f.key === k)
      return field ? t(field.labelKey) : k
    })

    const rows = pdfFiltered.map((r) => sortedFields.map((k) => getFieldValue(r, k)))

    autoTable(doc, {
      startY: 28,
      head: [headers],
      body: rows,
      styles: { fontSize: 8, cellPadding: 3, font: "DejaVuSans", fontStyle: "normal" },
      headStyles: { fillColor: [50, 50, 50], font: "DejaVuSans", fontStyle: "normal" },
    })

    doc.save(`logs_${label.replace(/\s+/g, "_")}${suffix}_${dateStr}.pdf`)
    setPdfModalOpen(false)
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("search_logs")}
            className="h-10 w-full max-w-sm rounded-md border border-input bg-background pl-10 pr-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        <div className="flex items-center gap-3">
          <Dialog
            open={clearModalOpen}
            onOpenChange={(v) => {
              if (!v) {
                setClearClient("")
                setClearConfirmText("")
              }
              setClearModalOpen(v)
            }}
          >
            <DialogTrigger asChild>
              <button className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent transition-colors">
                <Trash2 className="h-4 w-4" />
                {t("btn_clear_logs")}
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{t("dialog_clear_logs")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">{t("clear_logs_select_client")}</p>
                <select
                  value={clearClient}
                  onChange={(e) => setClearClient(e.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">{t("clear_logs_all")}</option>
                  {uniqueClients.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <div className="space-y-1.5">
                  <label className="text-sm text-muted-foreground">
                    Type <span className="font-mono font-medium text-foreground">delete logs</span>{" "}
                    to confirm
                  </label>
                  <input
                    value={clearConfirmText}
                    onChange={(e) => setClearConfirmText(e.target.value)}
                    placeholder='type "delete logs"'
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
                <p className="text-sm">
                  {t("clear_logs_count").replace("{n}", String(clearLogsCount))}
                </p>
                <div className="flex justify-end gap-2">
                  <DialogClose asChild>
                    <button className="h-9 px-4 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent transition-colors">
                      {t("btn_cancel")}
                    </button>
                  </DialogClose>
                  <button
                    onClick={handleClearLogs}
                    disabled={clearConfirmText !== "delete logs"}
                    className="h-9 px-4 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("btn_delete")}
                  </button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">{t("filter_all_clients")}</option>
            {uniqueClients.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <button
            onClick={openSendReportModal}
            disabled={!clientFilter || sendingReport}
            title={t("btn_send_report")}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="h-4 w-4" />
            {sendingReport ? t("report_sending") : t("btn_send_report")}
          </button>
          <Dialog
            open={sendModalOpen}
            onOpenChange={(v) => {
              if (!v) {
                setSendConfirmText("")
              }
              setSendModalOpen(v)
            }}
          >
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{t("dialog_send_report")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {t("th_client")}:{" "}
                  <span className="font-medium text-foreground">{clientFilter}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Type <span className="font-mono font-medium text-foreground">Send</span> to
                  confirm
                </p>
                <input
                  value={sendConfirmText}
                  onChange={(e) => setSendConfirmText(e.target.value)}
                  placeholder="Send"
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                <div className="flex justify-end gap-2">
                  <DialogClose asChild>
                    <button className="h-9 px-4 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent transition-colors">
                      {t("btn_cancel")}
                    </button>
                  </DialogClose>
                  <button
                    onClick={handleSendReportToTelegram}
                    disabled={sendConfirmText !== "Send" || sendingReport}
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="h-4 w-4" />
                    {sendingReport ? t("report_sending") : t("btn_send_report")}
                  </button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <button
            onClick={openDuplicatesModal}
            title={t("btn_duplicates")}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent transition-colors"
          >
            <Copy className="h-4 w-4" />
            {t("btn_duplicates")}
            {dupData && dupData.total > 0 && (
              <span className="ml-1 inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                {dupData.total}
              </span>
            )}
          </button>
          <Dialog
            open={dupModalOpen}
            onOpenChange={(v) => {
              if (!v) setDupConfirmText("")
              setDupModalOpen(v)
            }}
          >
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{t("dialog_duplicates")}</DialogTitle>
              </DialogHeader>
              {dupLoading ? (
                <p className="text-sm text-muted-foreground">{t("duplicates_loading")}</p>
              ) : dupData && dupData.total > 0 ? (
                <div className="space-y-4">
                  <p className="text-sm">
                    {t("duplicates_total").replace("{n}", String(dupData.total))}
                  </p>
                  {clientFilter ? (
                    <p className="text-sm text-muted-foreground">
                      {t("th_client")}:{" "}
                      <span className="font-medium text-foreground">{clientFilter}</span>
                    </p>
                  ) : null}
                  <div className="rounded-md border overflow-x-auto max-h-64">
                    <table className="text-xs w-full whitespace-nowrap">
                      <thead>
                        <tr className="border-b bg-muted/50 text-left">
                          <th className="px-2 py-1.5 font-medium">{t("th_account")}</th>
                          <th className="px-2 py-1.5 font-medium">{t("th_client")}</th>
                          <th className="px-2 py-1.5 font-medium">{t("th_name")}</th>
                          <th className="px-2 py-1.5 font-medium">#</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dupData.groups.map((g) => (
                          <tr key={`${g.accountId}|${g.keepId}`} className="border-b last:border-0">
                            <td className="px-2 py-1.5 font-mono">{g.accountId || "—"}</td>
                            <td className="px-2 py-1.5">{g.client || "—"}</td>
                            <td
                              className="px-2 py-1.5 whitespace-normal break-words"
                              title={g.details}
                            >
                              {g.details || "—"}
                            </td>
                            <td className="px-2 py-1.5 font-semibold">×{g.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Type{" "}
                    <span className="font-mono font-medium text-foreground">delete duplicates</span>{" "}
                    to confirm
                  </p>
                  <input
                    value={dupConfirmText}
                    onChange={(e) => setDupConfirmText(e.target.value)}
                    placeholder="delete duplicates"
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                  <div className="flex justify-end gap-2">
                    <DialogClose asChild>
                      <button className="h-9 px-4 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent transition-colors">
                        {t("btn_cancel")}
                      </button>
                    </DialogClose>
                    <button
                      onClick={handleRemoveDuplicates}
                      disabled={dupConfirmText !== "delete duplicates" || removingDup}
                      className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="h-4 w-4" />
                      {removingDup ? t("duplicates_removing") : t("btn_delete")}
                    </button>
                  </div>
                </div>
              ) : dupData ? (
                <p className="text-sm text-muted-foreground">{t("duplicates_none")}</p>
              ) : null}
            </DialogContent>
          </Dialog>
          <Dialog open={pdfModalOpen} onOpenChange={setPdfModalOpen}>
            <DialogTrigger asChild>
              <button className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent transition-colors">
                <Download className="h-4 w-4" />
                {t("btn_download_pdf")}
              </button>
            </DialogTrigger>
            <DialogContent className="!w-fit min-w-[560px] !max-w-[calc(100vw-2rem)]">
              <DialogHeader>
                <DialogTitle>{t("btn_download_pdf")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">{t("pdf_select_fields")}</p>
                <p className="text-sm">
                  {t("th_client")}:{" "}
                  <span className="font-medium">{clientFilter || t("filter_all_clients")}</span>
                </p>
                <div className="flex flex-wrap gap-4">
                  {PDF_FIELDS.map((field) => (
                    <label
                      key={field.key}
                      className="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <Switch
                        checked={selectedFields.includes(field.key)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedFields((prev) => [...prev, field.key])
                          } else {
                            setSelectedFields((prev) => prev.filter((k) => k !== field.key))
                          }
                        }}
                      />
                      {t(field.labelKey)}
                    </label>
                  ))}
                </div>
                {selectedFields.length > 0 && pdfFiltered.length > 0 && (
                  <div className="rounded-md border overflow-x-auto max-h-64">
                    <table className="text-xs whitespace-nowrap">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          {sortedFields.map((k) => {
                            const field = PDF_FIELDS.find((f) => f.key === k)
                            return (
                              <th key={k} className="px-2 py-1.5 text-left font-medium">
                                {field ? t(field.labelKey) : k}
                              </th>
                            )
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {pdfFiltered.slice(0, 5).map((r) => (
                          <tr key={r.id} className="border-b last:border-0">
                            {sortedFields.map((k) => (
                              <td key={k} className="px-2 py-1.5">
                                {getFieldValue(r, k)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {selectedFields.length > 0 && pdfFiltered.length > 5 && (
                  <p className="text-xs text-muted-foreground text-center">
                    ...{t("pdf_preview_more")}
                  </p>
                )}
                <div className="flex justify-end gap-2">
                  <DialogClose asChild>
                    <button className="h-9 px-4 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent transition-colors">
                      {t("btn_cancel")}
                    </button>
                  </DialogClose>
                  <button
                    onClick={handleDownloadPdf}
                    disabled={selectedFields.length === 0}
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    <Download className="h-4 w-4" />
                    {t("btn_generate")}
                  </button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-44">{t("th_time")}</TableHead>
              <TableHead className="w-24">{t("th_user")}</TableHead>
              <TableHead className="w-24">{t("th_page")}</TableHead>
              <TableHead className="w-20">{t("th_action")}</TableHead>
              <TableHead className="w-36">{t("th_client")}</TableHead>
              <TableHead className="w-48">{t("th_account_email")}</TableHead>
              <TableHead className="w-20">{t("th_sqft")}</TableHead>
              <TableHead className="w-20">{t("th_price")}</TableHead>
              <TableHead className="w-16">{t("th_rooms")}</TableHead>
              <TableHead>{t("th_name")}</TableHead>
              <TableHead>{t("th_location")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center text-muted-foreground py-8">
                  {t("empty_no_logs")}
                </TableCell>
              </TableRow>
            ) : (
              paged.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-mono text-sm text-muted-foreground whitespace-nowrap">
                    {row.timestamp}
                  </TableCell>
                  <TableCell>{row.user}</TableCell>
                  <TableCell className="capitalize">{row.page}</TableCell>
                  <TableCell>
                    <Badge variant={ACTION_VARIANT[row.action]}>
                      {t(ACTION_T_KEY[row.action])}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {row.client || <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>
                    {row.accountEmail ? (
                      <span className="text-sm">{row.accountEmail}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  {(() => {
                    const parsed = parseDetails(row.details)
                    return parsed ? (
                      <>
                        <TableCell className="text-sm whitespace-nowrap">{parsed.size}</TableCell>
                        <TableCell className="text-sm whitespace-nowrap">{parsed.price}</TableCell>
                        <TableCell className="text-sm">{parsed.rooms}</TableCell>
                        <TableCell className="text-sm">{parsed.name}</TableCell>
                        <TableCell className="text-sm">{parsed.location}</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell className="text-sm text-muted-foreground">—</TableCell>
                        <TableCell className="text-sm text-muted-foreground">—</TableCell>
                        <TableCell className="text-sm text-muted-foreground">—</TableCell>
                        <TableCell className="text-sm text-muted-foreground">—</TableCell>
                        <TableCell className="text-sm text-muted-foreground">—</TableCell>
                      </>
                    )
                  })()}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {filtered.length > 0 && (
          <div className="flex items-center justify-between border-t px-4 py-3 text-sm">
            <span className="text-muted-foreground">
              {t(
                "pagination_summary",
                String((currentPage - 1) * PAGE_SIZE + 1),
                String(Math.min(currentPage * PAGE_SIZE, filtered.length)),
                String(filtered.length),
              )}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage <= 1}
                className="inline-flex h-8 items-center gap-1 rounded-md border border-input bg-background px-3 text-sm font-medium transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
                {t("pagination_prev")}
              </button>
              <span className="text-muted-foreground">
                {t("pagination_page", String(currentPage), String(totalPages))}
              </span>
              <button
                type="button"
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="inline-flex h-8 items-center gap-1 rounded-md border border-input bg-background px-3 text-sm font-medium transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t("pagination_next")}
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Logs
